import { Container, Placeholder } from '@lmn/design-system';
import { useState } from 'react';
import styled, { css } from 'styled-components';
import {
  ChangeTransportHeader,
  ChangeTransportHeaderMobile,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportHeader';
import { ChangeTransportSubheaderMenu } from '@/app/components/hotel-detail/changeTransport/ChangeTransportSubheaderMenu';

const FLIGHT_ICON_SIZE = '40px';
const PROVIDER_PLACEHOLDER_SIZE = '32px';
const TEXT_PLACEHOLDER_SIZE = '16px';

const Column = styled.div<{ width: string; justify?: string }>`
  height: 100%;
  width: ${({ width }) => width || '100%'};
  justify-content: ${({ justify }) => justify || 'center'};
  display: flex;
  flex-direction: column;

  > div {
    max-width: 100%;
  }
`;

const DesktopTemplateContainer = styled.div`
  height: 170px;
  position: relative;
  background-color: white;
  border-radius: 8px;
  display: flex;
`;

const RowPlaceholderContainer = styled.div`
  max-width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`;

const DesktopContentPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 24px 16px;
  gap: 32px;
  flex-grow: 1;
`;

const DesktopPricePlaceholder = styled.div`
  width: 206px;
  padding: 24px 16px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  box-shadow:
    0 0 20px rgba(0, 0, 0, 0.04),
    0 0 10px rgba(0, 0, 0, 0.04),
    0 0 4px rgba(0, 0, 0, 0.02);
`;

const MobileTemplateContainer = styled.div`
  height: 320px;
  position: relative;
  background-color: white;
  border-radius: 8px;
`;

const MobilePricePlaceholder = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;
  padding: 15px 16px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const RowMobileAirlinesPlaceholderContainer = styled.div`
  display: flex;
  width: 100%;
  height: 40px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 0 8px;
`;

const RowMobileFlightPlaceholderContainer = styled.div`
  display: flex;
  width: 100%;
  height: 50px;
  flex-direction: row;
  justify-content: space-between;
  padding: 0 8px;
`;

const RowMobileDepartureTimePlaceholderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const RowMobileDurationPlaceholderContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
`;

const RowPlaceholder = () => (
  <RowPlaceholderContainer>
    <Column width={FLIGHT_ICON_SIZE} style={{ marginLeft: '16px' }}>
      <Placeholder height={FLIGHT_ICON_SIZE} width={FLIGHT_ICON_SIZE} />
    </Column>
    <Column width="80%" style={{ marginLeft: '16px' }}>
      <Placeholder height={FLIGHT_ICON_SIZE} width="100%" />
    </Column>
    <Column width={'80px'} style={{ padding: '0 8px', alignItems: 'center' }}>
      <Placeholder height="20px" width={FLIGHT_ICON_SIZE} />
    </Column>
  </RowPlaceholderContainer>
);

const RowMobileAirlinesPlaceholder = () => (
  <RowMobileAirlinesPlaceholderContainer>
    <Placeholder height="20px" width="150px" />
    <Placeholder height="20px" width="60px" />
  </RowMobileAirlinesPlaceholderContainer>
);

const RowMobileFlightPlaceholder = () => (
  <RowMobileFlightPlaceholderContainer>
    <RowMobileDepartureTimePlaceholderContainer>
      <Placeholder height="20px" width="100px" />
      <Placeholder height="20px" width="175px" />
    </RowMobileDepartureTimePlaceholderContainer>
    <RowMobileDurationPlaceholderContainer>
      <Placeholder height="20px" width="100px" />
      <Placeholder height="20px" width="50px" />
    </RowMobileDurationPlaceholderContainer>
  </RowMobileFlightPlaceholderContainer>
);

export const MobileFlightPlaceholder = () => (
  <div
    css={css`
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 8px;
      padding: 8px 16px 10px 16px;
      border-bottom: 1px solid ${({ theme }) => theme.commons.colors.gray50};
    `}
  >
    <RowMobileAirlinesPlaceholder />
    <div
      css={css`
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        gap: 16px;
      `}
    >
      <RowMobileFlightPlaceholder />
      <RowMobileFlightPlaceholder />
    </div>
  </div>
);

const DesktopTemplate = () => (
  <DesktopTemplateContainer>
    <DesktopContentPlaceholder>
      <RowPlaceholder />
      <RowPlaceholder />
    </DesktopContentPlaceholder>
    <DesktopPricePlaceholder>
      <Placeholder height="40px" width="100%" />
    </DesktopPricePlaceholder>
  </DesktopTemplateContainer>
);
const MobileTemplate = () => (
  <MobileTemplateContainer>
    <MobileFlightPlaceholder />
    <MobilePricePlaceholder>
      <Placeholder height="40px" width="100%" />
    </MobilePricePlaceholder>
  </MobileTemplateContainer>
);

const SortContainerPlaceholder = styled.div`
  background-color: white;
  display: flex;
  width: 100%;
  margin-bottom: 16px;
  border-radius: 8px;
`;

const SortTabPlaceholderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
  gap: 4px;
  padding: 8px;
  height: 60px;
  &:not(:last-child) {
    border-right: 1px solid ${(p) => p.theme.commons.colors.gray100};
  }
`;

const SortTabPlaceholder = () => (
  <SortTabPlaceholderContainer>
    <Placeholder height="18px" width="100px" />
    <Placeholder height="18px" width="35px" />
  </SortTabPlaceholderContainer>
);

const FlightCardPlaceholder = ({ isMobile }: FlightPlaceholderProps) =>
  isMobile ? <MobileTemplate /> : <DesktopTemplate />;

const TransportSortPlaceholder = () => (
  <SortContainerPlaceholder>
    {[1, 2, 3].map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <SortTabPlaceholder key={index} />
    ))}
  </SortContainerPlaceholder>
);

type FlightPlaceholderProps = {
  isMobile: boolean;
};
const FlightPlaceholders = ({ isMobile = false }: { isMobile?: boolean }) => (
  <div
    css={`
      display: flex;
      flex-direction: column;
      flex-grow: 1;
      gap: 16px;
    `}
  >
    {!isMobile && <TransportSortPlaceholder />}
    {Array.from({ length: isMobile ? 3 : 6 }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <FlightCardPlaceholder key={`flight-card-placeholder-${index}`} isMobile={isMobile} />
    ))}
  </div>
);

function randomIntFromInterval(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1) + min);
}

type FlightFilterPlaceholderProps = {
  placeholders?: number;
  showTitle?: boolean;
};

const FlightFilterContentPlaceHolder = styled.div`
  display: flex;
  flex-direction: column;
  width: 260px;
`;

const FlightFilterTitlePlaceholder = () => (
  <div css={{ display: 'flex', gap: '16px', marginBottom: '16px', marginTop: '28px' }}>
    <Placeholder height={PROVIDER_PLACEHOLDER_SIZE} width="60%" />
  </div>
);

const FlightFilterSummaryPlaceholder = () => (
  <div
    css={`
      display: flex;
      height: 60px;
      background-color: white;
      border-radius: 8px;
      align-items: center;
      padding: 16px;
    `}
  >
    <Placeholder height="20px" width="60%" />
  </div>
);

const FlightFilterOptionsPlaceholder = ({ $isFirst = false, $isLast = false }) => {
  const [width] = useState(() => randomIntFromInterval(40, 80));
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '16px',
        marginTop: $isFirst ? '32px' : '0',
        marginBottom: $isLast ? '0' : '16px',
      }}
    >
      <Placeholder height={TEXT_PLACEHOLDER_SIZE} width={`${width}%`} />
      <Placeholder height="20px" width="20px" />
    </div>
  );
};

const FlightFilterSubSectionPlaceholder = () => (
  <>
    <FlightFilterTitlePlaceholder />
    <FlightFilterOptionsPlaceholder $isFirst={true} />
    <FlightFilterTitlePlaceholder />
    <FlightFilterOptionsPlaceholder $isFirst={true} />
    <FlightFilterOptionsPlaceholder />
    <FlightFilterOptionsPlaceholder />
  </>
);

const FLightFilterSectionPlaceholder = () => (
  <Container className="trip-view-placeholder" radius="8px" fill="gray0" padding={2} margin={{ bottom: 2 }}>
    <FlightFilterSubSectionPlaceholder />
    <FlightFilterSubSectionPlaceholder />
  </Container>
);

const FlightFilterPlaceholder = ({ placeholders, showTitle }: FlightFilterPlaceholderProps) => {
  const [random] = useState(() => placeholders || randomIntFromInterval(2, 5));
  const items = new Array(random).fill(null);
  return (
    <Container className="trip-view-placeholder" radius="8px" fill="gray0" padding={2} margin={{ bottom: 2 }}>
      {showTitle && <FlightFilterTitlePlaceholder />}
      {items.map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <FlightFilterOptionsPlaceholder $isLast={i === items.length - 1} key={`flight-filter-placeholder-${i}`} />
      ))}
    </Container>
  );
};

const TransportsFilterPlaceholder = () => (
  <FlightFilterContentPlaceHolder>
    <FlightFilterSummaryPlaceholder />
    <FlightFilterTitlePlaceholder />
    <FlightFilterPlaceholder placeholders={1} />
    <FLightFilterSectionPlaceholder />
    <FLightFilterSectionPlaceholder />
  </FlightFilterContentPlaceHolder>
);

const DialogContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

const DialogContent = styled.div`
  display: flex;
  flex-direction: row;
  background-color: ${(p) => p.theme.commons.colors.gray50};
  padding: 24px;
  gap: 24px;
`;

const DialogContentMobile = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${(p) => p.theme.commons.colors.gray50};
  padding: 16px;
  gap: 16px;
`;

type PlaceholderLayoutProps = {
  isTrain: boolean;
  closeModal: () => void;
};

type ChangeTransportModalPlaceholdersProps = {
  isMobile?: boolean;
} & PlaceholderLayoutProps;

const PlaceholderDesktopLayout = (props: PlaceholderLayoutProps) => {
  const { isTrain, closeModal } = props;
  return (
    <DialogContainer>
      <ChangeTransportHeader isTrain={isTrain} closeModal={closeModal} />
      <DialogContent>
        <TransportsFilterPlaceholder />
        <FlightPlaceholders />
      </DialogContent>
    </DialogContainer>
  );
};

const PlaceholderMobileLayout = (props: PlaceholderLayoutProps) => {
  const { isTrain, closeModal } = props;
  return (
    <DialogContainer>
      <ChangeTransportHeaderMobile isTrain={isTrain} closeModal={closeModal}>
        <ChangeTransportSubheaderMenu
          isTrain={isTrain}
          openFilterModal={() => {}}
          openSortModal={() => {}}
          loading={true}
        />
      </ChangeTransportHeaderMobile>
      <DialogContentMobile>
        <FlightPlaceholders isMobile={true} />
      </DialogContentMobile>
    </DialogContainer>
  );
};

export const ChangeTransportModalPlaceholders = (props: ChangeTransportModalPlaceholdersProps) => {
  const { isMobile, ...rest } = props;
  return isMobile ? <PlaceholderMobileLayout {...rest} /> : <PlaceholderDesktopLayout {...rest} />;
};
